// Wall-construction learning (domain 'wall_construction'; framework in learningCore.js).
// Pure functions over the aggregates sp_GetWallLearningModel returns (db/86):
//   buildWallModel          rows -> lookup model
//   proposeWallLearning     model + existing rules + templates -> proposals an admin approves
//   predictWallConstruction model + one wall's facts -> a pre-fill (or null)
//   rankLearnedCandidates   model + one wall's facts -> every construction people have chosen there (chooser)
//   summarizeFillPerformance how often a person kept what a rule / wall system / prediction filled
// The model only ever contains HUMAN-DECIDED walls (see db/86), so nothing here can learn from the engine itself.
import { DEFAULT_LEARNING_TUNABLES, entityKeyOf, canvasTypeKey, pickWinner, meetsBar, proposalBar, predictionBar, pct } from "./learningCore";
import { parseWallSystemsJson, pairKey } from "../takeoffGeometry";

const num = (v) => (v == null || v === "" ? null : +v);

function toGroup(r, extra = {}) {
  const kind = r.Kind;
  const g = {
    kind, itemId: kind === "item" ? +r.ItemID : null, assemblyId: kind === "assembly" ? +r.AssemblyID : null,
    samples: +r.Samples || 0, projects: +r.Projects || 0,
    minGap: num(r.MinGap), maxGap: num(r.MaxGap), confirmed: +r.Confirmed || 0, corrected: +r.Corrected || 0, ...extra,
  };
  g.key = entityKeyOf(g);
  return g;
}

/** Tables 1-4 of sp_GetWallLearningModel -> { pairs: Map, templates: Map, performance, totals }. */
export function buildWallModel({ pairRows = [], templateRows = [], performanceRows = [], totalsRow = null } = {}) {
  const pairs = new Map();
  for (const r of pairRows) {
    const lo = +r.TplLo, hi = +r.TplHi, k = `${lo}:${hi}:${r.Rel}`;
    if (!pairs.has(k)) pairs.set(k, []);
    pairs.get(k).push(toGroup(r, { totalSamples: +r.PairSamples || 0, totalProjects: +r.PairProjects || 0 }));
  }
  const templates = new Map();
  for (const r of templateRows) {
    const k = `${+r.TemplateID}:${r.Rel}`;
    if (!templates.has(k)) templates.set(k, []);
    templates.get(k).push(toGroup(r, { totalSamples: +r.TplSamples || 0, totalProjects: +r.TplProjects || 0 }));
  }
  const performance = performanceRows.map(r => ({ source: r.Source, ruleId: r.RuleID == null ? null : +r.RuleID, accepted: +r.Accepted || 0, corrected: +r.Corrected || 0 }));
  const totals = totalsRow ? { decisions: +totalsRow.Decisions || 0, evidenceDecisions: +totalsRow.EvidenceDecisions || 0, projects: +totalsRow.Projects || 0 } : { decisions: 0, evidenceDecisions: 0, projects: 0 };
  return { pairs, templates, performance, totals };
}

export const mergeLearningTunables = (t) => ({ ...DEFAULT_LEARNING_TUNABLES, ...(t || {}) });

// A group "fits" a wall when the wall's gap lies inside the gap range that construction was seen at (± margin).
function inRange(g, gapFt, margin) {
  if (gapFt == null || g.minGap == null || g.maxGap == null) return true;
  return gapFt >= g.minGap - margin - 1e-9 && gapFt <= g.maxGap + margin + 1e-9;
}

function decide(groups, bar, basis) {
  if (!groups.length) return null;
  const w = pickWinner(groups);
  if (!w.top) return null;
  const projects = w.top.projects;
  const cand = { samples: w.top.samples, projects, agreement: w.agreement };
  if (!meetsBar(cand, bar)) return null;
  return {
    kind: w.top.kind, itemId: w.top.itemId, assemblyId: w.top.assemblyId, entityKey: w.top.key,
    samples: w.top.samples, total: w.total, projects, agreement: w.agreement, basis, source: "learned",
  };
}

/**
 * The construction people have chosen for a wall like this one, or null when the history is too thin / too split.
 * info = { rel: 'shared'|'cavity'|'exterior', gapFt, tplA, tplB }. Pair history first (same two room types, same
 * relationship, gap within the range each construction was seen at); then each template's own history for that kind
 * of wall (a shared wall needs both templates to agree, as wall systems do).
 */
export function predictWallConstruction(info, model, tunables) {
  const t = mergeLearningTunables(tunables);
  if (!model || !(+t.LEARN_PREDICT_ENABLED > 0) || !info || info.tplA == null) return null;
  const bar = predictionBar(t), margin = +t.LEARN_GAP_MARGIN_FT;

  if ((info.rel === "shared" || info.rel === "cavity") && info.tplB != null) {
    const groups = (model.pairs.get(`${Math.min(info.tplA, info.tplB)}:${Math.max(info.tplA, info.tplB)}:${info.rel}`) || []).filter(g => inRange(g, info.gapFt, margin));
    const d = decide(groups, bar, "pair");
    if (d) return d;
  }
  const own = (tpl) => decide(model.templates.get(`${tpl}:${info.rel}`) || [], bar, "template");
  if (info.rel === "shared" && info.tplB != null) {
    const a = own(info.tplA), b = own(info.tplB);
    if (a && b) return a.entityKey === b.entityKey ? a : null;
    return a || b || null;
  }
  return own(info.tplA);
}

/** Every construction people chose at a wall like this, most-supported first — the Review Walls chooser's "learned" row. */
export function rankLearnedCandidates(info, model, tunables) {
  const t = mergeLearningTunables(tunables);
  if (!model || !info || info.tplA == null) return [];
  const margin = +t.LEARN_GAP_MARGIN_FT;
  const out = [];
  const add = (groups, basis) => {
    const w = pickWinner(groups);
    for (const g of w.sorted) {
      const typeKey = canvasTypeKey(g);
      if (out.some(o => o.typeKey === typeKey)) continue;
      out.push({ typeKey, samples: g.samples, total: w.total, share: w.total ? g.samples / w.total : 0, projects: g.projects, basis });
    }
  };
  if ((info.rel === "shared" || info.rel === "cavity") && info.tplB != null) {
    add((model.pairs.get(`${Math.min(info.tplA, info.tplB)}:${Math.max(info.tplA, info.tplB)}:${info.rel}`) || []).filter(g => inRange(g, info.gapFt, margin)), "pair");
  }
  add(model.templates.get(`${info.tplA}:${info.rel}`) || [], "template");
  if (info.rel === "shared" && info.tplB != null) add(model.templates.get(`${info.tplB}:${info.rel}`) || [], "template");
  return out;
}

// Do two [min, max] gap ranges overlap? null = unbounded on that side.
function rangesOverlap(aMin, aMax, bMin, bMax) {
  return (aMax == null || bMin == null || bMin <= aMax) && (bMax == null || aMin == null || aMin <= bMax);
}
const round2 = (x) => Math.round(x * 100) / 100;
const REL_WORD = { shared: "shared wall", cavity: "cavity face", exterior: "exterior wall" };

/**
 * Turn the model into reviewable proposals.
 *   ctx = { rules: sp_GetConstructionStandardRules rows, templates: sp_GetRoomTemplates rows,
 *           entityName(kind,id), templateName(id) }
 * Returns { proposals, covered, conflicts, ambiguous }:
 *   proposals  new rules / template wall systems that clear the evidence bar
 *   covered    how many were already configured the same way (nothing to do)
 *   conflicts  history that DISAGREES with a configured rule / template system (shown, never overwritten)
 *   ambiguous  situations with plenty of history but no clear winner (shown so a human can decide the rule)
 */
export function proposeWallLearning(model, ctx = {}, tunables) {
  const t = mergeLearningTunables(tunables);
  const bar = proposalBar(t);
  const rules = (ctx.rules || []).filter(r => r.IsActive);
  const entityName = ctx.entityName || ((kind, id) => `${kind} ${id}`);
  const templateName = ctx.templateName || ((id) => `template ${id}`);
  const nameOf = (g) => entityName(g.kind, g.kind === "item" ? g.itemId : g.assemblyId);
  const proposals = [], conflicts = [], ambiguous = [];
  let covered = 0;

  // ── pair rules (Construction Standards) ──
  for (const [k, groups] of model.pairs) {
    const [loS, hiS, rel] = k.split(":");
    const lo = +loS, hi = +hiS;
    const w = pickWinner(groups);
    const pairProjects = groups[0]?.totalProjects || 0;
    const base = { lo, hi, rel, groups: w.sorted.map(g => ({ key: g.key, name: nameOf(g), samples: g.samples, projects: g.projects, minGap: g.minGap, maxGap: g.maxGap, confirmed: g.confirmed, corrected: g.corrected })), total: w.total, pairProjects };

    // candidate rules: [{ group, min, max, rangeKind }]
    let candidates = [];
    if (w.top && meetsBar({ samples: w.top.samples, projects: w.top.projects, agreement: w.agreement }, bar)) {
      candidates = [{ g: w.top, min: null, max: null, rangeKind: "any", agreement: w.agreement, samples: w.top.samples, projects: w.top.projects }];
    } else if (w.sorted.length >= 2 && w.sorted.every(g => g.minGap != null && g.maxGap != null)) {
      // split by gap: constructions seen at disjoint gap ranges are separate rules (a flush joint vs an 18" chase)
      const byGap = [...w.sorted].sort((a, b) => a.minGap - b.minGap);
      const disjoint = byGap.every((g, i) => i === 0 || g.minGap > byGap[i - 1].maxGap);
      if (disjoint) {
        candidates = byGap.map((g, i) => {
          const min = i === 0 ? null : round2((byGap[i - 1].maxGap + g.minGap) / 2);
          const max = i === byGap.length - 1 ? null : round2((g.maxGap + byGap[i + 1].minGap) / 2);
          return { g, min, max, rangeKind: "range", agreement: 1, samples: g.samples, projects: g.projects };
        }).filter(c => meetsBar({ samples: c.samples, projects: c.projects, agreement: 1 }, bar));
      }
    }
    if (!candidates.length) {
      if (w.total >= bar.minSamples) ambiguous.push({ kind: "pair", ...base, reason: w.agreement < bar.minAgreement ? "split" : "thin" });
      continue;
    }

    for (const c of candidates) {
      const clash = rules.filter(r => {
        const ra = r.RoomTemplateAID, rb = r.RoomTemplateBID;
        return pairKey(ra, rb) === pairKey(lo, hi) && (r.RelationshipType == null || r.RelationshipType === rel)
          && rangesOverlap(c.min, c.max, num(r.DistanceMinFt), num(r.DistanceMaxFt));
      });
      if (clash.some(r => entityKeyOf(r) === c.g.key)) { covered++; continue; }
      if (clash.length) { conflicts.push({ kind: "pair", ...base, proposedName: nameOf(c.g), rules: clash.map(r => ({ ruleId: r.RuleID, name: r.EntityName || entityKeyOf(r) })), agreement: c.agreement }); continue; }
      const rangeText = c.rangeKind === "range" ? ` (gap ${c.min == null ? "up to" : `${c.min}′ to`}${c.max == null ? (c.min == null ? " any" : " and up") : ` ${c.max}′`})` : "";
      proposals.push({
        domain: "wall_pair_rule",
        key: `pair:${lo}:${hi}|${rel}|${c.g.key}|${c.rangeKind}`,
        title: `${templateName(lo)} ↔ ${templateName(hi)}, ${REL_WORD[rel]}: ${nameOf(c.g)}${rangeText}`,
        proposal: { tplA: lo, tplB: hi, rel, kind: c.g.kind, itemId: c.g.itemId, assemblyId: c.g.assemblyId, min: c.min, max: c.max },
        evidence: base,
        samples: c.samples, projects: c.projects, agreement: round2(c.agreement * 10000) / 10000,
      });
    }
  }

  // ── template wall systems by position (Estimate Engine > Rooms) ──
  const tplById = new Map((ctx.templates || []).map(x => [x.RoomTemplateID, x]));
  for (const [k, groups] of model.templates) {
    const [idS, rel] = k.split(":");
    const tid = +idS;
    const w = pickWinner(groups);
    const projects = w.top?.projects || 0;
    const base = { templateId: tid, rel, groups: w.sorted.map(g => ({ key: g.key, name: nameOf(g), samples: g.samples, projects: g.projects })), total: w.total, tplProjects: groups[0]?.totalProjects || 0 };
    if (!w.top || !meetsBar({ samples: w.top.samples, projects, agreement: w.agreement }, bar)) {
      if (w.total >= bar.minSamples) ambiguous.push({ kind: "template", ...base, reason: w.agreement < bar.minAgreement ? "split" : "thin" });
      continue;
    }
    const tpl = tplById.get(tid);
    if (!tpl) continue;   // a retired / unknown template: nothing to attach a system to
    const current = parseWallSystemsJson(tpl.WallSystemsJson)[rel];
    const all = tpl.WallKind ? { kind: tpl.WallKind, itemId: tpl.WallItemID ?? null, assemblyId: tpl.WallAssemblyID ?? null } : null;
    const existing = current || all;
    if (existing && entityKeyOf(existing) === w.top.key) { covered++; continue; }
    if (current) { conflicts.push({ kind: "template", ...base, proposedName: nameOf(w.top), rules: [{ ruleId: null, name: `${templateName(tid)} ${REL_WORD[rel]} system` }], agreement: w.agreement }); continue; }
    proposals.push({
      domain: "wall_template_position",
      key: `tpl:${tid}|${rel}|${w.top.key}`,
      title: `${templateName(tid)}: ${REL_WORD[rel]} = ${nameOf(w.top)}${all ? " (replaces its all-walls system here)" : ""}`,
      proposal: { templateId: tid, position: rel, kind: w.top.kind, id: w.top.kind === "item" ? w.top.itemId : w.top.assemblyId },
      evidence: base,
      samples: w.top.samples, projects, agreement: round2(w.agreement * 10000) / 10000,
    });
  }

  proposals.sort((a, b) => b.samples - a.samples);
  return { proposals, covered, conflicts, ambiguous };
}

/** Per fill source (and per rule): how often a person kept what the engine filled. Flags a rule people keep changing. */
export function summarizeFillPerformance(performance, tunables) {
  const t = mergeLearningTunables(tunables);
  return (performance || []).map(p => {
    const n = p.accepted + p.corrected;
    const keptRate = n ? p.accepted / n : null;
    return { ...p, n, keptRate, keptText: keptRate == null ? "—" : pct(keptRate), underperforming: n >= t.LEARN_MIN_SAMPLES && keptRate < 0.6 };
  });
}
